import { Fragment, ReactNode, useEffect, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { Field, FormSection } from "@atlaskit/form";

import { Link } from "@/libraries/design/components/link/src/link";
import { useLocalizedHref } from "@/libraries/web-framework/src/localization/use-localized-href";
import { DEFAULT_VALUES } from "@/libraries/web-framework/src/localization/values";

import { useConsentHubClientConfig } from "../../../../../analytics/consent-hub/consent-client.tsx";

const privacyPolicyParagraphStyle = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#42526e"
});

const linkStyle = css({
    "&:hover": {
        // eslint-disable-next-line local/ensure-wac-token-usage
        color: "#000000",
        textShadow: "none",
        textDecoration: "underline"
    }
});

const consentBoxTextStyle = css({
    fontFamily:
        "Charlie Text, -apple-system, system-ui, Segoe UI, Roboto, Noto Sans, Ubuntu, Droid Sans, Helvetica Neue, sans-serif",
    fontSize: "16px",
    fontWeight: "500",
    lineHeight: "1.5",
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#42526e"
});

export const ConsentInfo = (
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    setFieldValue: (name: string, value: any) => void
) => {
    const locale = useConsentHubClientConfig();
    const intl = useIntl();
    const privacyLink = useLocalizedHref("/legal/privacy-policy");
    const [showConsent, setShowConsent] = useState(true);

    useEffect(() => {
        if (!locale.localeRequiresMarketingCommunicationOptIn) {
            setShowConsent(false);
            setFieldValue("consent", true);
        }
    }, [locale.localeRequiresMarketingCommunicationOptIn, setFieldValue]);

    const PrivacyPolicyComponent = (
        <div>
            <p css={privacyPolicyParagraphStyle}>
                <FormattedMessage
                    id={"enterpriseContactSales.privacyPolicy"}
                    defaultMessage={
                        "By submitting this form, I acknowledge receipt of Atlassian's <privacyPolicyLink>Privacy Policy</privacyPolicyLink>."
                    }
                    values={{
                        ...DEFAULT_VALUES,
                        privacyPolicyLink: (text: ReactNode) => (
                            // eslint-disable-next-line @atlassian/a11y/anchor-has-content, @atlassian/a11y/link-name
                            <Link
                                text={text as unknown as string}
                                href={privacyLink}
                                type={"inline"}
                                css={linkStyle}
                            />
                        )
                    }}
                />
            </p>
        </div>
    );
    const ConsentComponent = (
        <Field name={"consent"}>
            {({
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                fieldProps: { isRequired, isDisabled, isInvalid, ...validProps }
            }) => (
                <Fragment>
                    <input type={"checkbox"} {...validProps} />
                    <label htmlFor={"consent"} css={consentBoxTextStyle}>
                        {intl.formatMessage({
                            id: "enterpriseContactSales.consent",
                            defaultMessage:
                                "Yes! I would like to receive promotional emails including product news, events and more from Atlassian and its affiliates."
                        })}
                    </label>
                </Fragment>
            )}
        </Field>
    );

    return (
        <FormSection>
            <Fragment>
                {PrivacyPolicyComponent}
                {showConsent && ConsentComponent}
            </Fragment>
        </FormSection>
    );
};
